import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../supabaseClient';
import { useLanguage } from '../../../helpers/language-context';

const ScrollTimeline = () => {
  const [timelineData, setTimelineData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeYear, setActiveYear] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedYearFilter, setSelectedYearFilter] = useState('All');
  const { language } = useLanguage();
  const [yearsInView, setYearsInView] = useState(new Set());
  const sectionRefs = useRef({});

  useEffect(() => {
    const fetchTimeline = async () => {
      const { data, error } = await supabase
        .from('timeline')
        .select('*')
        .order('date', { ascending: false });

      if (error) {
        console.error('Error fetching timeline:', error);
      } else {
        // Prepare a flat list of events with their year info for alternating layout


        // Group data by year for tracking active year and entrance animations
        const groupedData = data.reduce((acc, item) => {
          const year = new Date(item.date).getFullYear().toString();
          if (!acc[year]) acc[year] = [];
          acc[year].push(item);
          return acc;
        }, {});

        const sortedYears = Object.keys(groupedData).sort((a, b) => b - a);
        const formattedData = sortedYears.map(year => ({
          year,
          events: groupedData[year]
        }));

        setTimelineData(formattedData);
        if (formattedData.length > 0) {
          setActiveYear(formattedData[0].year);
        }
      }
      setLoading(false);
    };

    fetchTimeline();
  }, []);

  useEffect(() => {
    if (loading || timelineData.length === 0) return;

    const activeObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveYear(entry.target.dataset.year);
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: '-20% 0px -20% 0px'
      }
    );

    const animationObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setYearsInView((prev) => {
              const next = new Set(prev);
              next.add(entry.target.dataset.year);
              return next;
            });
            animationObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) {
        activeObserver.observe(ref);
        animationObserver.observe(ref);
      }
    });

    return () => {
      activeObserver.disconnect();
      animationObserver.disconnect();
    };
  }, [loading, timelineData, selectedYearFilter]);

  const formatDate = (dateString, showDay = true) => {
    if (!dateString) return '';
    const options = showDay
      ? { day: 'numeric', month: 'long', year: 'numeric' }
      : { month: 'long', year: 'numeric' };
    const locale = language === 'en' ? 'en-GB' : 'ms-MY';
    return new Date(dateString).toLocaleDateString(locale, options);
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  // Calculate global index for alternating
  let globalEventCounter = 0;

  const displayedData = selectedYearFilter === 'All'
    ? timelineData
    : timelineData.filter(d => d.year === selectedYearFilter);

  return (
    <div className="w-full relative px-4 md:px-0 max-w-7xl mx-auto py-20 overflow-hidden">
      {/* Year Filter */}
      <div className="flex justify-center mb-16 relative z-10">
        <select
          value={selectedYearFilter}
          onChange={(e) => setSelectedYearFilter(e.target.value)}
          className="appearance-none bg-white border-2 border-orange-500 text-blue-900 font-bold py-3 px-8 pr-12 rounded-full shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-900 cursor-pointer text-lg hover:shadow-xl transition-all"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23f97316' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
            backgroundPosition: `right 1rem center`,
            backgroundRepeat: `no-repeat`,
            backgroundSize: `1.5em 1.5em`
          }}
        >
          <option value="All">{language === 'en' ? 'Show All Years' : 'Papar Semua Tahun'}</option>
          {timelineData.map((data) => (
            <option key={data.year} value={data.year}>
              {data.year}
            </option>
          ))}
        </select>
      </div>
      <div className="relative">
        {/* Centered Timeline Line */}
        <div className="absolute left-1/2 transform -translate-x-1/2 top-0 bottom-0 w-0.5 bg-gray-200 hidden md:block"></div>

        <div className="relative space-y-32">
          {displayedData.map((yearData) => (
            <div
              key={yearData.year}
              ref={(el) => (sectionRefs.current[yearData.year] = el)}
              data-year={yearData.year}
              className={`relative transition-all duration-1000 ease-out ${yearsInView.has(yearData.year) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
                }`}
            >
              {/* Year Marker on Line */}
              <div className="absolute left-1/2 transform -translate-x-1/2 -top-12 z-10 hidden md:block">
                <div className={`px-6 py-2 rounded-full text-white font-bold text-xl shadow-lg transition-colors duration-500 ${activeYear === yearData.year ? 'bg-orange-500' : 'bg-blue-900 opacity-50'
                  }`}>
                  {yearData.year}
                </div>
              </div>

              {/* Mobile Year Title */}
              <div className="md:hidden mb-8">
                <h2 className="text-4xl font-bold text-blue-900 border-l-4 border-orange-500 pl-4">
                  {yearData.year}
                </h2>
              </div>

              <div className="space-y-12 md:space-y-24">
                {yearData.events.map((event) => {
                  const isLeft = globalEventCounter % 2 === 0;
                  globalEventCounter++;
                  const content = language === 'en' ? event.event_en : event.event_ms;

                  return (
                    <div key={event.id} className="relative flex flex-col md:flex-row items-center justify-center w-full">
                      {/* Event Card */}
                      <div
                        className={`w-full md:w-[450px] h-[300px] bg-white rounded-2xl shadow-xl border border-gray-100 p-8 flex flex-col cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 group relative overflow-hidden ${isLeft ? 'md:mr-auto' : 'md:ml-auto'
                          }`}
                        onClick={() => setSelectedEvent(event)}
                      >
                        <div className="mb-4">
                          <span className="text-indigo-600 font-bold text-sm tracking-widest uppercase">
                            {formatDate(event.date)}
                          </span>
                          <div className="h-1 w-12 bg-orange-500 mt-2 transition-all duration-300 group-hover:w-24"></div>
                        </div>
                        <div className="flex-1 overflow-hidden">
                          <p className="text-gray-700 leading-relaxed text-lg line-clamp-5 whitespace-pre-line group-hover:text-blue-900 transition-colors">
                            {content}
                          </p>
                        </div>
                        <div className="mt-4 flex items-center text-blue-900 font-semibold text-sm">
                          <span>Read More</span>
                          <svg className="w-4 h-4 ml-2 transform group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </div>
                      </div>

                      {/* Dot on Line */}
                      <div className="absolute left-1/2 transform -translate-x-1/2 top-1/2 -translate-y-1/2 w-4 h-4 bg-orange-500 rounded-full border-4 border-white shadow-md hidden md:block"></div>

                      {/* Connecting Line */}
                      <div className={`absolute top-1/2 -translate-y-1/2 h-0.5 bg-gray-100 hidden md:block ${isLeft ? 'left-[calc(50%+2rem)] right-[calc(100%-450px)]' : 'right-[calc(50%+2rem)] left-[calc(100%-450px)] invisible'
                        }`} style={{ width: 'calc(50% - 225px - 2rem)' }}></div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal Tooltip / Full View */}
      {selectedEvent && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setSelectedEvent(null)}>
          <div
            className="bg-white rounded-3xl w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-2xl relative animate-scale-up"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-6 right-6 p-2 rounded-full hover:bg-gray-100 transition-colors z-10"
              onClick={() => setSelectedEvent(null)}
            >
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="p-8 md:p-12">
              <div className="mb-6">
                <span className="text-indigo-600 font-bold text-sm tracking-widest uppercase block mb-2">
                  {formatDate(selectedEvent.date)}
                </span>
                <h2 className="text-3xl font-bold text-blue-900">
                  {new Date(selectedEvent.date).getFullYear()}
                </h2>
                <div className="h-1.5 w-20 bg-orange-500 mt-4 rounded-full"></div>
              </div>
              <p className="text-gray-700 leading-relaxed text-xl whitespace-pre-line">
                {language === 'en' ? selectedEvent.event_en : selectedEvent.event_ms}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScrollTimeline;
